import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { CheckCircle, LogOut, UserCog } from 'lucide-react';
import DashboardFuncionario from './DashboardFuncionario';
import DashboardPersona from './DashboardPersona';

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Redirigir superadmin a su dashboard específico
  useEffect(() => {
    if (user?.user_type === 'superadmin') {
      navigate('/superadmin');
    } else if (user?.user_type === 'evaluador') {
      navigate('/evaluador');
    }
  }, [user, navigate]);

  // Si el usuario es funcionario, mostrar su dashboard específico
  if (user?.user_type === 'funcionario') {
    return <DashboardFuncionario />;
  }

  // Si el usuario es persona natural o jurídica, mostrar el dashboard de persona
  // Ambos tipos pueden ver convocatorias y postularse
  if (user?.user_type === 'natural' || user?.user_type === 'juridica') {
    return <DashboardPersona />;
  }

  // Si es superadmin, mostrar loading mientras redirige
  if (user?.user_type === 'superadmin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-muted-foreground">Redirigiendo al panel de Super Admin...</div>
      </div>
    );
  }

  // Si es evaluador, mostrar loading mientras redirige
  if (user?.user_type === 'evaluador') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-muted-foreground">Redirigiendo al panel de Evaluador...</div>
      </div>
    );
  }

  // Dashboard por defecto para persona jurídica u otros
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <img 
            src="https://customer-assets.emergentagent.com/job_login-verify-system/artifacts/gyieqwft_Captura%20de%20Pantalla%202025-12-10%20a%20la%28s%29%207.03.41%20a.%C2%A0m..png" 
            alt="Logo" 
            className="h-12 w-auto object-contain"
          />
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => navigate('/perfil/editar')}
              className="h-10 px-4 rounded-sm font-medium transition-all hover:bg-accent/10 hover:text-accent"
              data-testid="edit-profile-button"
            >
              <UserCog className="h-4 w-4 mr-2" />
              Editar Perfil
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="h-10 px-4 rounded-sm font-medium transition-all hover:bg-accent/10 hover:text-accent"
              data-testid="dashboard-logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Cerrar sesión
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-8">
          {/* Welcome Section */}
          <div className="space-y-2">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-primary">
              ¡Bienvenido, {user?.user_type === 'natural' 
                ? `${user?.first_name} ${user?.first_lastname}` 
                : user?.company_name}!
            </h1>
            <p className="text-lg text-muted-foreground">
              Tu cuenta ha sido verificada exitosamente
            </p>
          </div>

          {/* User Info Card */}
          <div className="bg-white border border-border rounded-sm p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="h-6 w-6 text-accent" />
              </div>
              <div className="space-y-4 flex-1">
                <div>
                  <h2 className="text-2xl font-semibold tracking-tight text-primary mb-2">
                    Información de la cuenta
                  </h2>
                  <p className="text-muted-foreground">
                    Tus datos registrados en la plataforma
                  </p>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  {user?.user_type === 'natural' ? (
                    <>
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-muted-foreground">Nombre completo</p>
                        <p className="text-base font-medium text-primary" data-testid="dashboard-user-name">
                          {user?.first_name} {user?.second_name && `${user.second_name} `}{user?.first_lastname} {user?.second_lastname}
                        </p>
                      </div>

                      <div className="space-y-1">
                        <p className="text-sm font-medium text-muted-foreground">Documento de identidad</p>
                        <p className="text-base font-medium text-primary" data-testid="dashboard-user-document">
                          {user?.document_type}: {user?.document_id}
                        </p>
                      </div>

                      {user?.phone && (
                        <div className="space-y-1">
                          <p className="text-sm font-medium text-muted-foreground">Teléfono</p>
                          <p className="text-base font-medium text-primary" data-testid="dashboard-user-phone">
                            {user?.phone}
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-muted-foreground">Razón Social</p>
                        <p className="text-base font-medium text-primary" data-testid="dashboard-company-name">
                          {user?.company_name}
                        </p>
                      </div>

                      <div className="space-y-1">
                        <p className="text-sm font-medium text-muted-foreground">NIT</p>
                        <p className="text-base font-medium text-primary" data-testid="dashboard-company-nit">
                          {user?.nit}
                        </p>
                      </div>

                      <div className="space-y-1">
                        <p className="text-sm font-medium text-muted-foreground">Representante Legal</p>
                        <p className="text-base font-medium text-primary" data-testid="dashboard-company-representative">
                          {user?.legal_representative}
                        </p>
                      </div>
                    </>
                  )}

                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Correo electrónico</p>
                    <p className="text-base font-medium text-primary" data-testid="dashboard-user-email">
                      {user?.email}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Estado de verificación</p>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <p className="text-base font-medium text-green-600" data-testid="dashboard-verification-status">
                        Verificado
                      </p>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Tipo de usuario</p>
                    <p className="text-base font-medium text-primary" data-testid="dashboard-user-type">
                      {user?.user_type === 'natural' ? 'Persona Natural' : 'Persona Jurídica'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Info Message */}
          <div className="bg-secondary/50 border border-border rounded-sm p-6">
            <p className="text-sm text-muted-foreground text-center">
              Esta es tu área de trabajo. Aquí podrás acceder a todas las funcionalidades de la plataforma.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}